import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { STUCK_AFTER_MS } from '../../../domain/interiorTutorial';
import { askSkipPractice, usePracticeRoom } from './practiceRoom';
import TouchHand from '../../../ui/primitives/TouchHand';
import './TutorialSpotlight.css';

export interface ScreenRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TutorialSpotlightProps {
  /** A DOM target: the element with data-tutorial="…". */
  target?: string;
  /** Or a screen rect (things drawn on the canvas). */
  rect?: ScreenRect | null;
  /** What the drawn hand shows on the target. */
  gesture?: 'tap' | 'drag';
  /** Drag gestures: where the hand carries the item to (screen px). */
  dragTo?: { x: number; y: number } | null;
  /** The guide's one line. */
  caption?: ReactNode;
  /** Only the target takes taps (nothing is darkened). */
  block?: boolean;
  /** Soft edge vignette — in the room, where the canvas is the stage. */
  vignette?: boolean;
  /** Progress dots: steps done so far / all steps. */
  progress?: { done: number; total: number };
  /** Leave the whole tutorial (behind a confirm). Omitted: no ✕. */
  onSkip?: () => void;
  /** Offered after STUCK_AFTER_MS without progress on `stepKey` — skips just this step. */
  onSkipStep?: () => void;
  /** Identifies the current step/phase; the stuck timer restarts when it changes. */
  stepKey?: string;
}

const PAD = 8;
/** Where the fingertip sits inside the 44 px hand drawing. */
const TIP = { x: 21, y: 11 };

export const ELDER_SRC = '/game-assets/nscene-characters/character-elder.png';

function useTargetRect(target: string | undefined, rect: ScreenRect | null | undefined): ScreenRect | null {
  const [measured, setMeasured] = useState<ScreenRect | null>(null);
  useEffect(() => {
    if (!target) return;
    let raf = 0;
    let scrolled = false;
    const tick = () => {
      const el = document.querySelector(`[data-tutorial="${target}"]`);
      const r = el?.getBoundingClientRect();
      if (el && r && !scrolled && (r.bottom > window.innerHeight - 140 || r.top < 0)) {
        scrolled = true;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      setMeasured((prev) => {
        if (!r || r.width === 0) return prev === null ? prev : null;
        if (prev && prev.x === r.x && prev.y === r.y && prev.width === r.width && prev.height === r.height) return prev;
        return { x: r.x, y: r.y, width: r.width, height: r.height };
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return target ? measured : rect ?? null;
}

function Hand() {
  return <TouchHand pathClassName="tutorial-hand__body" />;
}

/** The guide: the Elder and one line, with the tutorial's progress. */
export function TutorialGuideCard({ children, progress, atTop, action }: {
  children: ReactNode;
  progress?: { done: number; total: number };
  atTop?: boolean;
  action?: ReactNode;
}) {
  return (
    <div className={`tutorial-guide${atTop ? ' tutorial-guide--top' : ''}`}>
      <img className="tutorial-guide__elder" src={ELDER_SRC} alt="" />
      <div className="tutorial-guide__bubble">
        <span className="tutorial-guide__text">{children}</span>
        {action}
        {progress && (
          <span className="tutorial-guide__dots" aria-hidden="true">
            {Array.from({ length: progress.total }, (_, i) => (
              <i key={i} className={i < progress.done ? 'is-done' : i === progress.done ? 'is-now' : ''} />
            ))}
          </span>
        )}
      </div>
    </div>
  );
}

/** ✕ + "Skip the interior tutorial?" — the tutorial's way out, on every step. */
function TutorialExit({ onSkip }: { onSkip: () => void }) {
  const { t } = useTranslation();
  const asked = usePracticeRoom((s) => s.skipAsked);
  // Top right — beside the room's build switch when it is there (step 1 points at it).
  const [pos, setPos] = useState({ top: 16, right: 16 });
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const r = document.querySelector('[data-tutorial="build-toggle"]')?.getBoundingClientRect();
      const next = r && r.width > 0
        ? { top: Math.round(r.top + r.height / 2 - 20), right: Math.round(window.innerWidth - r.left + 10) }
        : { top: 16, right: 16 };
      setPos((prev) => (prev.top === next.top && prev.right === next.right ? prev : next));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <>
      <button type="button" className="tutorial-exit" style={pos} aria-label={t("Skip the interior tutorial?")} onClick={() => askSkipPractice()}>
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
      {asked && (
        <div className="tutorial-confirm" role="dialog" aria-modal="true">
          <div className="tutorial-confirm__card">
            <img className="tutorial-confirm__elder" src={ELDER_SRC} alt="" />
            <p className="tutorial-confirm__title">{t("Skip the interior tutorial?")}</p>
            <p className="tutorial-confirm__text">{t("You can furnish your home any time.")}</p>
            <div className="tutorial-confirm__buttons">
              <button type="button" className="is-primary" onClick={() => askSkipPractice(false)}>{t("Stay")}</button>
              <button type="button" onClick={onSkip}>{t("Skip")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The interior tutorial's pointer: a gold ring on the target and a drawn hand
 * performing the gesture on it, the guide's line and the way out. The game stays
 * fully lit — focus comes from the ring, not from darkening the screen.
 */
export default function TutorialSpotlight({
  target, rect, gesture = 'tap', dragTo, caption, block = false, vignette = false, progress,
  onSkip, onSkipStep, stepKey = '',
}: TutorialSpotlightProps) {
  const { t } = useTranslation();
  const hole = useTargetRect(target, rect);
  // Never a dead end: a step that makes no progress offers "Skip step".
  const [stuckKey, setStuckKey] = useState<string | null>(null);
  useEffect(() => {
    const id = window.setTimeout(() => setStuckKey(stepKey), STUCK_AFTER_MS);
    return () => window.clearTimeout(id);
  }, [stepKey]);
  const canSkipStep = !!onSkipStep && stuckKey === stepKey;

  const h = hole && { x: hole.x - PAD, y: hole.y - PAD, w: hole.width + 2 * PAD, h: hole.height + 2 * PAD };
  const cx = hole ? hole.x + hole.width / 2 : 0;
  const cy = hole ? hole.y + hole.height / 2 : 0;
  // Keep the guide away from what it points at.
  const guideAtTop = !!hole && cy > window.innerHeight * 0.55;

  return createPortal(
    <div className="tutorial-spotlight" aria-live="polite">
      {vignette && <div className="tutorial-spotlight__vignette" />}
      {block && h && (
        <>
          <div className="tutorial-spotlight__block" style={{ left: 0, top: 0, right: 0, height: Math.max(0, h.y) }} />
          <div className="tutorial-spotlight__block" style={{ left: 0, top: h.y + h.h, right: 0, bottom: 0 }} />
          <div className="tutorial-spotlight__block" style={{ left: 0, top: h.y, width: Math.max(0, h.x), height: h.h }} />
          <div className="tutorial-spotlight__block" style={{ left: h.x + h.w, top: h.y, right: 0, height: h.h }} />
        </>
      )}
      {h && (
        <div className="tutorial-spotlight__ring" style={{ left: h.x, top: h.y, width: h.w, height: h.h }}>
          <i className="tutorial-spotlight__spark" />
          <i className="tutorial-spotlight__spark" />
          <i className="tutorial-spotlight__spark" />
        </div>
      )}
      {hole && (
        <div
          className={`tutorial-hand tutorial-hand--${gesture}`}
          style={{
            left: cx - TIP.x,
            top: cy - TIP.y,
            ['--tut-dx' as string]: `${dragTo ? dragTo.x - cx : 0}px`,
            ['--tut-dy' as string]: `${dragTo ? dragTo.y - cy : 0}px`,
          }}
        >
          <span className="tutorial-hand__ripple" style={{ left: TIP.x, top: TIP.y }} />
          <Hand />
        </div>
      )}
      {caption && (
        <TutorialGuideCard
          progress={progress}
          atTop={guideAtTop}
          action={canSkipStep && (
            <button type="button" className="tutorial-guide__skip-step" onClick={onSkipStep}>
              {t("Skip step")}
            </button>
          )}
        >
          {caption}
        </TutorialGuideCard>
      )}
      {onSkip && <TutorialExit onSkip={onSkip} />}
    </div>,
    document.body,
  );
}
